import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { errorMessage } from "../../api/errorMessage";

export type AccessibleSalon = { id: number; idApi?: number | string | null; title?: string | null; legalName?: string | null; city?: string | null; address?: string | null };
export type SalonResource<T> = { company: AccessibleSalon; data: T | null; loading: boolean; error: string };
type Loader<T> = (company: AccessibleSalon, signal: AbortSignal, refresh: boolean) => Promise<T>;
export function salonAddress(company: AccessibleSalon): string {
    return [company.city, company.address].filter(Boolean).join(", ") || company.title || "Салон №" + company.id;
}

/** Server membership list; at most three initial reads at once, independent of the header selection. */
export function useAccessibleSalons<T>(loadResource: Loader<T>) {
    const [rows, setRows] = useState<SalonResource<T>[]>([]);
    const [listing, setListing] = useState(true);
    const [error, setError] = useState("");
    const epoch = useRef(0);
    const listRequest = useRef<AbortController | null>(null);
    const requests = useRef(new Map<number, AbortController>());
    const companies = useRef<AccessibleSalon[]>([]);
    const abortAll = useCallback(() => {
        listRequest.current?.abort();
        requests.current.forEach(request => request.abort()); requests.current.clear();
    }, []);
    const read = useCallback(async (company: AccessibleSalon, generation: number, refresh: boolean) => {
        if (generation !== epoch.current) return;
        requests.current.get(company.id)?.abort();
        const controller = new AbortController(); requests.current.set(company.id, controller);
        const current = () => !controller.signal.aborted && generation === epoch.current && requests.current.get(company.id) === controller;
        setRows(previous => previous.map(row => row.company.id === company.id ? { ...row, loading: true, error: "" } : row));
        try {
            const data = await loadResource(company, controller.signal, refresh);
            if (current()) setRows(previous => previous.map(row => row.company.id === company.id ? { ...row, data, loading: false, error: "" } : row));
        } catch (err) {
            if (current()) {
                const failure = err as { status?: number; response?: { status?: number } };
                const denied = [401, 403].includes(Number(failure.status || failure.response?.status));
                setRows(previous => previous.map(row => row.company.id === company.id ? { ...row, data: denied ? null : row.data, loading: false, error: errorMessage(err, "Не удалось получить данные салона. Попробуйте обновить.") } : row));
            }
        } finally { if (requests.current.get(company.id) === controller) requests.current.delete(company.id); }
    }, [loadResource]);
    const reload = useCallback(async (refresh = false) => {
        abortAll(); const generation = ++epoch.current;
        const controller = new AbortController(); listRequest.current = controller;
        companies.current = []; setRows([]); setError(""); setListing(true);
        try {
            const response = await api.get<{ items?: AccessibleSalon[] }>("/api/me/companies", undefined, { signal: controller.signal, timeout: 25000 });
            if (controller.signal.aborted || generation !== epoch.current) return;
            if (!Array.isArray(response?.items)) throw new Error("Не удалось получить список доступных салонов.");
            const unique = new Map<number, AccessibleSalon>();
            response.items.forEach(company => {
                const id = Number(company?.id);
                if (Number.isSafeInteger(id) && id > 0 && !unique.has(id)) unique.set(id, { ...company, id });
            });
            const list = Array.from(unique.values()); companies.current = list;
            setRows(list.map(company => ({ company, data: null, loading: true, error: "" }))); setListing(false);
            let next = 0;
            const worker = async () => {
                while (!controller.signal.aborted && generation === epoch.current && next < list.length) {
                    const company = list[next++]; await read(company, generation, refresh);
                }
            };
            await Promise.all(Array.from({ length: Math.min(3, list.length) }, worker));
        } catch (err) {
            if (!controller.signal.aborted && generation === epoch.current) { setRows([]); companies.current = []; setError(errorMessage(err, "Не удалось загрузить доступные салоны.")); }
        } finally { if (!controller.signal.aborted && generation === epoch.current) setListing(false); }
    }, [abortAll, read]);
    useEffect(() => {
        const lifecycle = epoch;
        void reload();
        return () => { ++lifecycle.current; abortAll(); };
    }, [reload, abortAll]);
    const reloadCompany = (id: number, refresh = false) => {
        const company = companies.current.find(item => item.id === id);
        if (company) void read(company, epoch.current, refresh);
    };
    return { rows, listing, error, loading: listing || rows.some(row => row.loading), reload, reloadCompany };
}
